import {
  ElevatedSurface,
  IconButton,
  SingleSelectDropdown,
  type SingleSelectDropdownOption,
} from "@carbon6/c6-frontend-design";
import { useMemo } from "react";
import { EpisodeInspector } from "@/modules/inspect/public";
import { formatDateTime } from "@/shared/utils/datetime/format";
import type { Probe } from "../../domain/probe";
import { probeStatusView } from "../../feature/probeStatus";
import { DatasetFactsSummary } from "./DatasetFactsSection";
import cardStyles from "./DetailCard.module.css";
import styles from "./EpisodeInspectSection.module.css";

type EpisodeInspectSectionProps = {
  inspectHeaderHost?: HTMLElement | null;
  datasetUuid?: string;
  /** Probe 섹션이 보고 있는 건. 점검은 이 건의 샘플을 따른다(한 축이 두 섹션을 같이 움직인다). */
  probe: Probe | null;
  /** 이력 전체 — 샘플이 있는 다른 건으로 가는 길을 내기 위해서다. */
  probes: readonly Probe[];
  /** 창 최소화 같은 비활성 구간 — 뷰어가 재생을 멈춘다. */
  active: boolean;
  /** 고른 episode. 상세가 들어 섹션을 옮겨도 같은 episode 를 본다. `null` 이면 첫 episode. */
  selected: string | null;
  onSelect: (sourceId: string) => void;
  onOpenProbe: (probeUuid: string) => void;
};

/**
 * `점검·매핑` 섹션의 껍데기 — current Probe 의 episode 를 고르고 점검 뷰어를 연다.
 *
 * 샘플이 없으면 왜 없는지와 샘플 있는 건으로 가는 길을 말한다. 고른 episode 는 상세가 들어
 * 다른 섹션에 다녀와도 유지된다.
 */
export function EpisodeInspectSection({
  inspectHeaderHost,
  active,
  datasetUuid,
  onOpenProbe,
  onSelect,
  probe,
  probes,
  selected,
}: EpisodeInspectSectionProps) {
  const sourceIds = useMemo(
    () => (probe?.state === "sample_ready" ? (probe.selectedSourceIds ?? []) : []),
    [probe],
  );
  // 고른 episode 가 이 건에 없으면(다른 Probe 로 옮겼다) 그 건의 첫 episode 를 본다.
  const current =
    selected !== null && sourceIds.includes(selected) ? selected : (sourceIds[0] ?? null);

  const options = useMemo<SingleSelectDropdownOption[]>(
    () => sourceIds.map((sourceId) => ({ value: sourceId, label: sourceId })),
    [sourceIds],
  );

  if (probe === null || probe.state !== "sample_ready") {
    return <NoSampleCard probe={probe} probes={probes} onOpenProbe={onOpenProbe} />;
  }

  if (sourceIds.length === 0 || current === null) {
    return (
      <ElevatedSurface
        as="section"
        className={cardStyles.card}
        elevation={1}
        hoverElevation={false}
      >
        <p className={cardStyles.empty}>이 Probe 가 고른 episode 가 없습니다.</p>
      </ElevatedSurface>
    );
  }

  // 카드 · 타이틀 없이 평평하게 — 어느 episode 인지는 드롭다운이 이미 말한다. 드롭다운은 뷰어의
  // 사실 줄에 얹어 한 줄로 세운다(둘 다 "지금 무엇을 보고 있는가" 이고, 줄을 나누면 뷰어가 그만큼
  // 낮아진다). 뷰어는 남은 높이를 전부 받아 자기 구역들로 나눈다(재생바가 바닥에 서야 한다).
  const picker = (
    <SingleSelectDropdown
      label="episode"
      options={options}
      value={current}
      onValueChange={onSelect}
    />
  );
  return (
    <EpisodeInspector
      inspectHeaderHost={inspectHeaderHost}
      datasetUuid={datasetUuid}
      key={`${probe.uuid}:${current}`}
      active={active}
      picker={picker}
      // 조사 사실 요약은 datasets 가 만든다 — inspect 는 datasets 를 모른다(import 방향).
      factsSummary={
        datasetUuid === undefined ? undefined : (
          <DatasetFactsSummary datasetUuid={datasetUuid} active={active} />
        )
      }
      probeUuid={probe.uuid}
      revisionUuid={probe.revision?.uuid ?? null}
      sampleSourceIds={sourceIds}
      sourceId={current}
    />
  );
}

/** 샘플이 없는 이유와 다음 길 — 진행 중이면 기다리라고, 실패면 재요청하라고, 다른 건에 샘플이 있으면 그리로. */
function NoSampleCard({
  onOpenProbe,
  probe,
  probes,
}: {
  probe: Probe | null;
  probes: readonly Probe[];
  onOpenProbe: (probeUuid: string) => void;
}) {
  const withSample = probes.filter(
    (candidate) => candidate.state === "sample_ready" && candidate.uuid !== probe?.uuid,
  );
  const reason =
    probe === null
      ? "아직 확보한 샘플이 없습니다. `Probe 요청` 으로 앞 episode 의 샘플을 확보합니다."
      : probe.state === "failed"
        ? "이 Probe 는 샘플 확보에 실패했습니다. Probe 섹션에서 사유를 확인하고 다시 요청해 주세요."
        : `이 Probe 는 아직 ${probeStatusView(probe.state).label} 상태입니다. 샘플이 확보되면 여기서 점검할 수 있습니다.`;

  return (
    <ElevatedSurface as="section" className={cardStyles.card} elevation={1} hoverElevation={false}>
      <p className={cardStyles.empty}>{reason}</p>
      {withSample.length === 0 ? null : (
        <div className={styles.alternatives}>
          <p className={cardStyles.caption}>샘플을 확보한 다른 Probe 로 옮겨 점검할 수 있습니다.</p>
          <ul className={styles.alternativeList}>
            {withSample.map((candidate) => (
              <li key={candidate.uuid}>
                <IconButton
                  size="sm"
                  text={`${formatDateTime(candidate.createdAtMs)} 요청 · episode ${candidate.selectedSourceIds?.length ?? 0}개`}
                  variant="surface"
                  onClick={() => onOpenProbe(candidate.uuid)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </ElevatedSurface>
  );
}
